// Quarterly bonus arbitration (prototype's SbQuarterView): one card
// per employee — the quarter's three monthly notes, suggested bonus
// (Σ provisoires), final bonus decision, informed toggle. Cards sort
// by suggested desc; a focusId (from the month table) scrolls + flashes.

import { useEffect, useRef } from 'react';
import type { PayrollEmployeeDTO, PayrollMonthDTO } from '@pointage/shared';
import { usePatchPayrollBonus, usePatchPayrollInformed } from '../../api/hooks.js';
import { toast } from '../../store/toast.js';
import { fmtDA } from '../../lib/format.js';
import { MoneyButton, entityColor, entityShort, monthLabel, roleLabel, sbAvColor, sbInitials } from './common.js';
import { tagBg, tagColor } from './NoteDrawer.js';

export function QuarterView({
  data,
  team,
  canEdit,
  focusId,
  onClearFocus,
}: {
  data: PayrollMonthDTO;
  team: PayrollEmployeeDTO[];
  canEdit: boolean; // payroll.edit && quarter not sent
  focusId: string | null;
  onClearFocus: () => void;
}) {
  const patchBonus = usePatchPayrollBonus();
  const patchInformed = usePatchPayrollInformed();
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  const sorted = [...team].sort(
    (a, b) => b.suggestedBonus - a.suggestedBonus || a.name.localeCompare(b.name),
  );

  useEffect(() => {
    if (focusId && refs.current[focusId]) {
      const el = refs.current[focusId]!;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('sb-flash');
      const t = setTimeout(() => el.classList.remove('sb-flash'), 1600);
      onClearFocus();
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  const fail = (err: unknown) => {
    const e = err as Error & { body?: { error?: string; message?: string } };
    toast.danger(e.body?.message ?? (e.body?.error === 'locked' ? 'Trimestre envoyé — bonus verrouillé.' : 'Échec de l\'enregistrement.'));
  };

  const totFinal = team.reduce((a, e) => a + (e.finalBonus ?? 0), 0);
  const totSuggested = team.reduce((a, e) => a + e.suggestedBonus, 0);
  const defined = team.filter((e) => e.finalBonus != null).length;
  const q = data.quarter;

  return (
    <div>
      <div className="sb-qbar">
        <div className="sb-qkpi">
          <span className="sb-qkpi-v gm-mono">{defined}<i>/{team.length}</i></span>
          <span className="sb-qkpi-l">bonus arrêtés</span>
        </div>
        <div className="sb-qkpi">
          <span className="sb-qkpi-v gm-mono">{fmtDA(totFinal)} DA</span>
          <span className="sb-qkpi-l">enveloppe T{q} actuelle</span>
        </div>
        <div className="sb-qkpi">
          <span className="sb-qkpi-v gm-mono">{fmtDA(totSuggested)} DA</span>
          <span className="sb-qkpi-l">cumul des provisoires</span>
        </div>
        <div className="sb-qhint">
          Le bonus final part avec le <b>salaire de {monthLabel(data.quarterMonths[2]!).toLowerCase()}</b> — une seule ligne banque par personne.
        </div>
      </div>
      <div className="sb-qgrid">
        {sorted.map((e) => {
          const fin = e.finalBonus;
          return (
            <div key={e.id} className="sb-qcard" ref={(el) => { refs.current[e.id] = el; }}>
              <div className="sb-qhead">
                <span className="gm-av" style={{ background: sbAvColor(e.id) }}>{sbInitials(e.name)}</span>
                <div className="sb-qid">
                  <span className="sb-qname">{e.name}</span>
                  <span className="sb-qrole">
                    {roleLabel(e)} · <b style={{ color: entityColor(e.entityId) }}>{entityShort(e.entityId)}</b> ·{' '}
                    <span className="gm-mono">{fmtDA(e.salary)} DA</span>
                  </span>
                </div>
                <button
                  type="button"
                  className={'sb-inf sm' + (e.informed ? ' on' : '')}
                  onClick={() =>
                    patchInformed.mutateAsync({ year: data.year, quarter: q, employeeId: e.id, informed: !e.informed }).catch(fail)
                  }
                  title="Employé informé du bonus"
                >
                  {e.informed ? '✓ Informé' : 'Informé ?'}
                </button>
              </div>
              <div className="sb-qmonths">
                {data.quarterMonths.map((m) => {
                  const n = e.notes[m];
                  const has = !!n && (n.text !== '' || n.amount != null || n.tags.length > 0);
                  return (
                    <div key={m} className={'sb-qm' + (has ? '' : ' empty')}>
                      <div className="sb-qm-h">
                        <span>{monthLabel(m)}</span>
                        {n?.amount != null ? <b className="gm-mono">+{fmtDA(n.amount)}</b> : <b className="sb-qm-z">·</b>}
                      </div>
                      {has ? (
                        <>
                          {n!.text && <p className="sb-qm-t">{n!.text}</p>}
                          {n!.tags.length > 0 && (
                            <div className="sb-qm-tags">
                              {n!.tags.map((k) => (
                                <span key={k} className="sb-minitag" style={{ background: tagBg(k), color: tagColor(k) }}>
                                  {k === 'augm' ? 'Augmentation ?' : k.charAt(0).toUpperCase() + k.slice(1)}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="sb-qm-t none">aucune note</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="sb-qfoot">
                <div className="sb-qsugg">
                  <span>Suggéré (cumul)</span>
                  <b className="gm-mono">{fmtDA(e.suggestedBonus)} DA</b>
                  {canEdit && e.suggestedBonus > 0 && fin !== e.suggestedBonus && (
                    <button
                      type="button"
                      className="sb-qapply"
                      onClick={() =>
                        patchBonus.mutateAsync({ year: data.year, quarter: q, employeeId: e.id, amount: e.suggestedBonus }).catch(fail)
                      }
                    >
                      ↳ appliquer
                    </button>
                  )}
                </div>
                <div className="sb-qfinal">
                  <span>Bonus final T{q}</span>
                  {canEdit ? (
                    <MoneyButton
                      value={fin}
                      editable
                      onChange={(v) =>
                        patchBonus.mutateAsync({ year: data.year, quarter: q, employeeId: e.id, amount: v }).catch(fail)
                      }
                    />
                  ) : (
                    <b className="gm-mono">{fmtDA(fin)}</b>
                  )}
                  {fin != null && canEdit && (
                    <button
                      type="button"
                      className="sb-qclear"
                      title="Retirer le bonus"
                      onClick={() =>
                        patchBonus.mutateAsync({ year: data.year, quarter: q, employeeId: e.id, amount: null }).catch(fail)
                      }
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
